export type SharedSupportScript = {
  title: string;
  description: string;
  body: string;
};

export type SharedSurvivalPack = {
  title: string;
  summary: string;
  steps: string[];
  scriptTitle: string;
  scriptBody: string;
};

export function buildMoneyDebtScripts(firstName: string, creditor: string) {
  return [
    {
      title: 'Request breathing room',
      description: 'Use when you need charges paused and time to get advice.',
      body: `Hello ${creditor},\n\nI am writing because I am struggling to keep up with my current payments. I want to deal with this responsibly and avoid the account getting worse. Please pause fees and interest while I get advice and confirm what temporary support you can offer.\n\nThank you,\n${firstName}`,
    },
    {
      title: 'Ask for a repayment plan',
      description: 'Use when you can pay something, but not the current amount.',
      body: `Hello ${creditor},\n\nI am reviewing my finances and need a more affordable repayment arrangement. My current circumstances mean I cannot keep up with the existing payment level. Please let me know what information you need from me to agree a temporary reduced payment plan.\n\nThank you,\n${firstName}`,
    },
    {
      title: 'Offer a token payment',
      description: 'Use when you need a short holding arrangement while you get advice.',
      body: `Hello ${creditor},\n\nMy finances are under significant pressure and I am taking debt advice. For the short term, I can only offer a token payment while I stabilise essentials and get proper support. Please confirm whether you can accept this and pause collections pressure, fees, and interest while I do that.\n\nThank you,\n${firstName}`,
    },
    {
      title: 'Ask for fees and interest to stop',
      description: 'Use when a plan may be possible only if charges stop getting added.',
      body: `Hello ${creditor},\n\nI am trying to agree an affordable arrangement, but ongoing fees and interest are making the balance harder to control. Please confirm whether you can freeze fees and interest while I provide my income and spending information and agree a workable plan.\n\nThank you,\n${firstName}`,
    },
    {
      title: 'Ask to move the payment date',
      description: 'Use when the amount may be manageable but the timing is wrong.',
      body: `Hello ${creditor},\n\nI am trying to keep this account stable, but the current payment date no longer fits when my income lands. Please can you tell me whether the payment date can be moved so I have a better chance of keeping to the arrangement?\n\nThank you,\n${firstName}`,
    },
    {
      title: 'Push back on collections pressure',
      description: 'Use when contact is escalating but you are trying to engage responsibly.',
      body: `Hello ${creditor},\n\nI am trying to deal with this account responsibly and I am not ignoring it. Repeated pressure is making it harder to manage. Please note that I am reviewing my finances now and need a clear, workable response about temporary support or an affordable arrangement.\n\nThank you,\n${firstName}`,
    },
  ] satisfies SharedSupportScript[];
}

export function buildMoneyBenefitsScripts(firstName: string) {
  return [
    {
      title: 'Apply by phone',
      description: 'Use when you need to make the first call without freezing on the opening line.',
      body: `Hello, my name is ${firstName}. I want to check whether I should apply for this support and what information you need from me to do it properly. Please can you talk me through the process step by step, confirm what evidence I should have ready, and tell me whether there are any deadlines I need to know about before the application is submitted?`,
    },
    {
      title: 'Report a change of circumstances',
      description: 'Use when work, housing, health, or family circumstances have changed.',
      body: `Hello, my name is ${firstName}. I need to report a change of circumstances and I want to make sure it is recorded clearly. Please can you confirm what details you need from me now, when the change takes effect, whether anything else needs to be sent after this call, and when I should expect the update to appear on my claim?`,
    },
    {
      title: 'Mandatory reconsideration',
      description: 'Use when you think a decision was wrong and want the review logged cleanly.',
      body: `Hello, my name is ${firstName}. I am asking for a mandatory reconsideration because I think the decision on my claim does not reflect my circumstances properly. Please can you confirm how to register that request today, what deadline applies, where I should send extra evidence, and how I will know the reconsideration has been logged?`,
    },
    {
      title: 'Appeal a decision',
      description: 'Use when the reconsideration stage is over and you need the next step.',
      body: `Hello, my name is ${firstName}. I want to appeal this decision and I need to understand the next step clearly. Please can you confirm the appeal route, the deadline, whether I should include the mandatory reconsideration notice, and exactly what I should send so I do not miss anything important?`,
    },
    {
      title: 'Chase a delayed decision',
      description: 'Use when a claim, review, or evidence update has gone quiet.',
      body: `Hello, my name is ${firstName}. I am calling because I am still waiting for a decision or an update on my claim. Please can you confirm the current status, whether anything is still outstanding from me, and when I should realistically expect the next decision or response?`,
    },
    {
      title: 'Ask about backdating or overpayment',
      description: 'Use when you think you have missed money or an overpayment is being raised.',
      body: `Hello, my name is ${firstName}. I need help understanding whether this award can be backdated or whether an overpayment decision is being made correctly. Please can you explain what rule is being applied, what evidence would help, and what deadline I need to act by if I disagree?`,
    },
    {
      title: 'HMRC tax credits call',
      description: 'Use when older tax-credits issues still need a human answer.',
      body: `Hello, my name is ${firstName}. I am calling about tax credits and I need help understanding my current position. Please can you confirm whether my record shows any action I need to take, whether a change needs to be reported, whether there are any deadlines or overpayment issues I should deal with now, and what I should send if the record is wrong?`,
    },
  ] satisfies SharedSupportScript[];
}

export function buildMoneySurvivalPacks(firstName: string) {
  return [
    {
      title: 'First 24 hours',
      summary: 'Stabilise the immediate pressure before you try to solve everything.',
      steps: [
        'Check what has to be protected first: housing, council tax, energy, food, key travel, medication, and childcare.',
        'Open the letters or app alerts you are avoiding so you know what is urgent and what can wait a day.',
        'Use one holding script early instead of waiting for panic or shame to make the first contact harder.',
      ],
      scriptTitle: 'Short holding message',
      scriptBody: `Hello,\n\nI am dealing with financial pressure and I am trying to handle this responsibly before it gets worse. I need a short pause or a temporary arrangement while I review essentials and get advice. Please let me know the best next step.\n\nThank you,\n${firstName}`,
    },
    {
      title: 'This week',
      summary: 'Get practical support moving before another due date lands.',
      steps: [
        'Run the benefits check and look for emergency grants or local support.',
        'Use the statement tools to see what is actually leaving your account, not just what you think is happening.',
        'Tell any creditor or provider you are struggling before the next missed payment where possible.',
      ],
      scriptTitle: 'Utility hardship request',
      scriptBody: `Hello,\n\nI am struggling to keep up with my current household costs and I need to talk about hardship support or a temporary payment arrangement. Please tell me what support is available, what evidence you need from me, and whether you can put a short hold on collections or disconnection activity while this is reviewed.\n\nThank you,\n${firstName}`,
    },
    {
      title: 'Protect the basics',
      summary: 'Use this if council tax, rent, mortgage, or essential bills are starting to wobble.',
      steps: [
        'Ask the council about hardship relief or breathing room before arrears build.',
        'If rent or mortgage is under pressure, get advice early rather than hoping the next month will somehow fix it.',
        'Keep one short note of what changed financially so you can reuse it across calls and forms.',
      ],
      scriptTitle: 'Council tax hardship message',
      scriptBody: `Hello,\n\nI am getting in touch because I am struggling financially and I need to ask about council tax hardship support or a temporary arrangement before arrears grow. Please let me know what help may be available, whether recovery action can be paused while I apply, and what information you need from me now.\n\nThank you,\n${firstName}`,
    },
    {
      title: 'Keep the roof safe',
      summary: 'Use this when rent or mortgage pressure is moving from worry into arrears risk.',
      steps: [
        'Ask for the exact arrears balance, the next action date, and what would pause enforcement while you engage.',
        'Get advice before agreeing a repayment promise you cannot keep next month.',
        'Keep a dated note of every call, email, and offer so the paper trail stays usable.',
      ],
      scriptTitle: 'Rent or mortgage pressure message',
      scriptBody: `Hello,\n\nI am getting in touch because my housing costs are becoming hard to keep up with and I want to act before the situation gets worse. Please can you confirm my current balance, whether there is a temporary arrangement or support route available, and what I need to do today to avoid further action while I get advice?\n\nThank you,\n${firstName}`,
    },
    {
      title: 'Keep essential services on',
      summary: 'Use this when phone, broadband, or other essential household services may be cut or restricted.',
      steps: [
        'Tell the provider early if the service is essential for work, school, healthcare, or contact with children.',
        'Ask whether a social tariff, essential-service policy, or short-term arrangement applies.',
        'Keep the exact next payment date visible so this does not drift back out of sight.',
      ],
      scriptTitle: 'Essential service support message',
      scriptBody: `Hello,\n\nI am struggling financially and I need to keep this service running because it is important for day-to-day life. Please can you tell me whether there is a social tariff, hardship route, or temporary payment arrangement available, and whether any action can be paused while this is reviewed?\n\nThank you,\n${firstName}`,
    },
  ] satisfies SharedSurvivalPack[];
}
